import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'

import { MatchConfidence, ModelConfidence } from '../matching/types'
import { CsvColumn } from '../useCsvModal'
import { Badge, Variant } from '@/Atomic/Atoms/Badge'
import { Icon } from '@/Atomic/Atoms/Icon'
import { BasicSelect } from '@/Atomic/Atoms/Inputs/BasicSelect'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'

const CSV_CONFIDENCE_TOOLTIP_ID = 'csv-confidence-tooltip'
const CSV_EMAIL_KEY_TOOLTIP_ID = 'csv-email-key-tooltip'

// Every auto-filled column carries a chip so the user can tell what the system
// mapped from what they mapped by hand (no chip = manual). There are two kinds:
//   • "AI"    — the AI layer filled it; colour = the model's self-reported
//               confidence (high/medium/low).
//   • "Match" — the offline matcher filled it; colour = the match tier
//               (exact/normalised are certain → high; fuzzy/alias/abbreviation
//               are a guess → medium).
// Both are coloured green/amber/red on the same scale and explained on hover.
const CONFIDENCE_VARIANT: Record<ModelConfidence, Variant> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
}

// The offline match tiers, mapped to the same confidence scale as the AI chip.
const MATCH_TIER_CONFIDENCE: Record<
  Exclude<MatchConfidence, 'ai'>,
  ModelConfidence
> = {
  exact: 'high',
  normalised: 'high',
  fuzzy: 'medium',
}

interface MapFieldsStepProps {
  csvColumns: CsvColumn[]
  columnMappings: Record<number, string>
  columnMatchConfidence: Record<number, MatchConfidence>
  columnModelConfidence: Record<number, ModelConfidence>
  isMatchingAI: boolean
  useFirstRowAsHeaders: boolean
  entityAttributes: Option[]
  useEmailAsKey: boolean
  hasEmailTarget: boolean
  onHeadersToggle: (value: boolean) => void
  onEmailKeyToggle: (value: boolean) => void
  onMappingChange: (index: number, value: string) => void
}

interface Chip {
  label: string
  variant: Variant
  confidence: ModelConfidence
}

/**
 * Resolve the provenance chip for one column. Returns null when the column was
 * not auto-filled (i.e. the user mapped it) — no chip is the "you did this"
 * signal. AI matches colour by the model's confidence; offline matches colour by
 * their tier. Both fall back to 'medium' when a finer signal is absent.
 */
function resolveChip(
  matchConfidence: MatchConfidence | undefined,
  modelConfidence: ModelConfidence | undefined,
  t: (key: string) => string,
): Chip | null {
  if (!matchConfidence) {
    return null
  }
  const kind = matchConfidence === 'ai' ? 'ai' : 'match'
  const confidence =
    kind === 'ai'
      ? (modelConfidence ?? 'medium')
      : MATCH_TIER_CONFIDENCE[matchConfidence as Exclude<MatchConfidence, 'ai'>]
  return {
    label: t(`components.csv.matching.badge.${kind}`),
    variant: CONFIDENCE_VARIANT[confidence],
    confidence,
  }
}

export const MapFieldsStep: React.FC<MapFieldsStepProps> = ({
  csvColumns,
  columnMappings,
  columnMatchConfidence,
  columnModelConfidence,
  isMatchingAI,
  useFirstRowAsHeaders,
  entityAttributes,
  useEmailAsKey,
  hasEmailTarget,
  onHeadersToggle,
  onEmailKeyToggle,
  onMappingChange,
}) => {
  const { t } = useTranslation()

  const usedAttributes = useMemo(
    () => new Set(Object.values(columnMappings).filter(Boolean)),
    [columnMappings],
  )

  return (
    <div className="csv-map-step">
      {isMatchingAI && (
        <p className="csv-map-matching wizard-modal-meta">
          {t('components.csv.matching.analysing')}
        </p>
      )}
      <label className="csv-toggle wizard-modal-label">
        <input
          type="checkbox"
          checked={useFirstRowAsHeaders}
          onChange={(e) => onHeadersToggle(e.target.checked)}
        />
        <span className="csv-toggle-track" />
        {t('components.csv.useFirstRowAsHeaders')}
      </label>
      {hasEmailTarget && (
        <div className="csv-toggle-row">
          <label className="csv-toggle wizard-modal-label">
            <input
              type="checkbox"
              checked={useEmailAsKey}
              onChange={(e) => onEmailKeyToggle(e.target.checked)}
            />
            <span className="csv-toggle-track" />
            {t('components.csv.useEmailAsKey')}
          </label>
          <button
            type="button"
            className="csv-toggle-info"
            aria-label={t('components.csv.emailKeyInfoLabel')}
            data-tooltip-id={CSV_EMAIL_KEY_TOOLTIP_ID}
            data-tooltip-content={t('components.csv.emailKeyInfo')}
          >
            <Icon icon="info-circle" size="sm" />
          </button>
        </div>
      )}
      <table className="csv-map-table">
        <thead>
          <tr>
            <th>{t('components.csv.csvColumns')}</th>
            <th />
            <th>{t('components.csv.mapToAttribute')}</th>
            <th />
            <th>{t('components.csv.exampleData')}</th>
          </tr>
        </thead>
        <tbody>
          {csvColumns.map((col) => {
            const currentValue = columnMappings[col.rawIndex]
            const availableOptions = entityAttributes.filter(
              (a) =>
                a.value === currentValue ||
                !usedAttributes.has(String(a.value)),
            )
            const chip = resolveChip(
              columnMatchConfidence[col.rawIndex],
              columnModelConfidence[col.rawIndex],
              t,
            )
            return (
              <tr key={col.rawIndex}>
                <td>{col.header}</td>
                <td className="csv-map-arrow">→</td>
                <td>
                  <BasicSelect
                    name={`map-col-${col.rawIndex}`}
                    options={availableOptions}
                    value={
                      entityAttributes.find((a) => a.value === currentValue) ??
                      null
                    }
                    onChange={(opt) =>
                      onMappingChange(
                        col.rawIndex,
                        (opt as SingleValue<Option>)?.value?.toString() ?? '',
                      )
                    }
                    placeholder={t('components.csv.selectAttribute')}
                    isClearable
                    isSearchable
                  />
                </td>
                <td className="csv-map-badge-cell">
                  {chip && (
                    <span
                      className="csv-map-badge"
                      data-tooltip-id={CSV_CONFIDENCE_TOOLTIP_ID}
                      data-tooltip-content={t(
                        `components.csv.matching.confidence.${chip.confidence}`,
                      )}
                    >
                      <Badge variant={chip.variant} value={chip.label} />
                    </span>
                  )}
                </td>
                <td className="csv-map-example wizard-modal-meta">
                  {col.exampleValue}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <Tooltip id={CSV_CONFIDENCE_TOOLTIP_ID} size="s" />
      <Tooltip id={CSV_EMAIL_KEY_TOOLTIP_ID} place="right" size="s" />
    </div>
  )
}
